import { useMemo } from 'react';

import { EmptyState, Spinner } from '../../components';
import { ChartBarIcon } from '../../layout/icons';
import type { Id } from '../../api/types';
import { useQltSlotAttendanceStore } from '../../stores/qlt';
import { rosterOf } from '../submissions/submissions';
import { countedExams } from './classExams';
import { IZ_SIGNED_OUT, useExamResultStore } from './store';
import { useExamListLoad } from './useExamResults';
import '../../styles/submissions.css';

export interface ExamsPanelProps {
  classTitle: string;
  /** The Kho bài tree query; its courses name the exams to read. */
  classId: Id;
  gradeId?: Id;
  subjectId?: Id;
  /** Opens the exam's đề as a tab with the class's results beside it. */
  onOpen: (examId: string) => void;
  /** The exam whose tab is on the board. */
  activeExamId?: string | null;
}

// Bài kiểm tra: the IZ unit exams this period's class sat; counts only, the card beside the đề reads the attempts
export function ExamsPanel({ classTitle, classId, gradeId, subjectId, onOpen, activeExamId }: ExamsPanelProps) {
  const rows = useQltSlotAttendanceStore((state) => state.rows);
  const rosterLoading = useQltSlotAttendanceStore((state) => state.loading);
  const loading = useExamResultStore((state) => state.loading);
  const error = useExamResultStore((state) => state.error);
  const listed = useExamResultStore((state) => state.exams);
  const counts = useExamResultStore((state) => state.counts);
  const counted = useExamResultStore((state) => state.counted);
  const titles = useExamResultStore((state) => state.courseTitles);
  const examTitles = useExamResultStore((state) => state.examTitles);
  const { reload } = useExamListLoad({ classId, gradeId, subjectId });

  const roster = useMemo(() => rosterOf(rows), [rows]);
  const exams = useMemo(() => countedExams(listed, counts), [listed, counts]);
  const progress = listed.length > 0 ? `Đang tìm bài kiểm tra của lớp… ${counted}/${listed.length}` : 'Đang tải danh sách bài kiểm tra…';

  if (roster.length === 0) {
    if (rosterLoading) return <Spinner label="Đang tải danh sách lớp…" />;
    return <EmptyState icon={<ChartBarIcon size={26} />} title="Chưa có danh sách lớp" description="Bảng chưa tải được danh sách học sinh của tiết này nên chưa tìm được bài kiểm tra." />;
  }
  if (error) return <EmptyState icon={<ChartBarIcon size={26} />} title={error === IZ_SIGNED_OUT ? 'Chưa đăng nhập IZTeach' : 'Chưa tải được kết quả'} description={error} action={<button type="button" className="secondary-button" onClick={reload}>Thử lại</button>} />;
  if (loading && exams.length === 0) return <Spinner label={progress} />;
  if (exams.length === 0) return <EmptyState icon={<ChartBarIcon size={26} />} title="Chưa có bài kiểm tra nào" description={`Học sinh lớp ${classTitle || 'này'} chưa làm bài kiểm tra nào trên IZTeach.`} action={<button type="button" className="secondary-button" onClick={reload}>Tải lại</button>} />;

  return (
    <div className="hw-panel">
      <div className="hw-panel__bar">
        <p>Bài kiểm tra học sinh lớp {classTitle || 'này'} đã làm trên IZTeach.</p>
        <button type="button" className="hw-panel__refresh" disabled={loading} onClick={reload}>{loading ? 'Đang tải…' : 'Tải lại'}</button>
      </div>
      <ul className="up-list hw-panel__list">
        {exams.map((exam) => (
          <li key={exam.examId}>
            <button type="button" className={exam.examId === activeExamId ? 'up-list__row is-selected' : 'up-list__row'} aria-current={exam.examId === activeExamId ? 'true' : undefined} onClick={() => onOpen(exam.examId)}>
              <span className="up-list__mark" aria-hidden={true}><ChartBarIcon size={18} /></span>
              <span className="up-list__text">
                <span className="up-list__name" title={examTitles[exam.examId] || 'Bài kiểm tra'}>{examTitles[exam.examId] || 'Bài kiểm tra'}</span>
                <span className="up-list__meta">{[`${exam.submitted}/${roster.length} đã nộp`, exam.doing ? `${exam.doing} đang làm` : ''].filter(Boolean).join(' • ')}</span>
                {titles[exam.courseId] ? <span className="up-list__meta" title={titles[exam.courseId]}>{titles[exam.courseId]}</span> : null}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {loading ? <div className="hw-panel__more"><Spinner size="small" label={progress} /></div> : null}
    </div>
  );
}
